import { createSlice } from '@reduxjs/toolkit'
import appApi from 'api'
import { RootState } from 'store'
import capitalize from 'lodash/capitalize'
import { ErrorResponseType, InitialState } from './types'
import { AppDispatch } from 'store'
import { clearCurrentUser } from 'utils/authStorage'

type Feature = {
  otpModal: {
    expirationTime: number,
    isOtpModalOpen: boolean,
    resendOtpCount: number,
    verifyOtpCount: number,
  },
}


type LogoutReason = 'inactivity' | 'session_revoked' | null

type InitAuthState = InitialState & {
  feature: Feature,
  logoutReason: LogoutReason,
}

const initialState: InitAuthState = {
  tokenExpired: null,
  currentUser: null,
  errorData: null,
  logoutReason: null,
  feature: {
    otpModal: {
      expirationTime: 300,
      isOtpModalOpen: false,
      resendOtpCount: 0,
      verifyOtpCount: 0,
    },
  },
}

const authSlice = createSlice({
  name: 'authReducer',
  initialState,
  reducers: {
    removeErrorData: (state) => {
      state.errorData = null
      if (!state.feature.otpModal.isOtpModalOpen)
        state.feature.otpModal.expirationTime = 300
    },
    setOptModalExpirationTime: (state, action) => {
      state.feature.otpModal.expirationTime = action.payload
    },
    setOptModalOpen: (state, action) => {
      state.feature.otpModal.isOtpModalOpen = action.payload
    },
    // resetAuthState: (state) => {
    //   state = initialState
    //   sessionStorage.removeItem('currentUser')
    // },
    setTokenExpired: (state, action) => {
      state.tokenExpired = action.payload
    },
    setLogoutReason: (state, action) => {
      state.logoutReason = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        appApi.endpoints.login.matchFulfilled,
        (state) => {
          const resendOtpCount = state.feature.otpModal.resendOtpCount
          state.feature.otpModal.resendOtpCount = resendOtpCount > 2 ? 0 : resendOtpCount + 1
          state.feature.otpModal.verifyOtpCount = 0
          state.tokenExpired = null
          state.logoutReason = null
        }
      )
      .addMatcher(
        appApi.endpoints.forgetCredentials.matchFulfilled,
        (state) => {
          const resendOtpCount = state.feature.otpModal.resendOtpCount
          state.feature.otpModal.resendOtpCount = resendOtpCount > 2 ? 0 : resendOtpCount + 1
          state.feature.otpModal.verifyOtpCount = 0
        }
      )
      .addMatcher(
        appApi.endpoints.login.matchRejected,
        (state, { payload }) => {
          state.errorData = payload as ErrorResponseType
          state.errorData!.errorFrom = 'login'
        }
      )
      .addMatcher(
        appApi.endpoints.verifyOtp.matchFulfilled,
        (state, { payload }) => {
          state.currentUser = payload
        }
      )
      .addMatcher(
        appApi.endpoints.verifyOtp.matchRejected,
        (state, { payload }) => {
          state.errorData = payload as ErrorResponseType
          state.errorData!.errorFrom = 'verifyOtp'
          state.feature.otpModal.verifyOtpCount += 1
        }
      )
      .addMatcher(
        appApi.endpoints.verifyForgetCredentials.matchRejected,
        (state, { payload }) => {
          state.errorData = payload as ErrorResponseType
          state.errorData!.errorFrom = 'verifyOtp'
          state.feature.otpModal.verifyOtpCount += 1
        }
      )
      .addMatcher(
        appApi.endpoints.verifyOtpV2.matchRejected,
        (state, { payload }) => {
          state.errorData = payload as ErrorResponseType
          state.errorData!.errorFrom = 'verifyOtp'
          state.feature.otpModal.verifyOtpCount += 1
        }
      )
      .addMatcher(
        appApi.endpoints.sendOtpNotification.matchFulfilled,
        (state) => {
          state.feature.otpModal.expirationTime = 300
        }
      )
  },
})

export const {
  removeErrorData,
  setOptModalExpirationTime,
  setOptModalOpen,
  setTokenExpired,
  setLogoutReason,
} = authSlice.actions

export const selectCurrentErrorMessage = (state: RootState) =>
  capitalize(state.authReducer.errorData?.data?.message) ||
  capitalize(state.authReducer.errorData?.data?.error) ||
  state.authReducer.errorData?.error

export default authSlice.reducer

export const isTokenExpired = (state: RootState) => state.authReducer.tokenExpired
export const selectLogoutReason = (state: RootState) => state.authReducer.logoutReason



export const logoutUser = () => (dispatch: AppDispatch) => {
  clearCurrentUser()
  sessionStorage.removeItem('selectedSavingsAccountNumber')
  dispatch(setTokenExpired(null))
  dispatch(setLogoutReason(null))
  dispatch(removeErrorData())
  dispatch(setOptModalOpen(false))
  dispatch(setOptModalExpirationTime(300))
  dispatch(appApi.util.resetApiState())
}
